import { useEffect, useState } from "react";
import {
  Button,
  Container,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  FormHelperText,
  FormControlLabel,
  Switch
} from "@mui/material";
import { handlePagingAll } from "../../../../components/api/APIUtilities";
import { userDisplayName } from "../EventFunctions";
import { postNotification } from "../../../../components/utilities/Notifications";
import MarkdownViewer from "../../../../components/utilities/MarkdownViewer";
import * as Yup from "yup";
import { Formik, FieldArray } from "formik";
import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import { postData } from "../../../../components/utilities/UtilityFunctions";

export function EventSoftScorePlayerEntry({
  organize,
  event,
  player,
  round,
  activeRound,
  setPlayer,
  softScoreId,
  setRefreshPlayers,
  postPairingSubmit,
  softScorePairing,
  parentParing
}) {
  const [softScorePairings, setSoftScorePairings] = useState(null);
  const [players, setPlayers] = useState(null);
  const [opponents, setOpponents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  async function fetchSoftScorePairings() {
    let filters = {
      eventId: event.id,
      pairingType: "SoftScorePairing"
    };
    if (player?.id) {
      filters.playerId = player.id;
    } else if (parentParing) {
      filters.parentParingId = parentParing.id;
      filters.byePlayer = true;
    }
    if (softScoreId) {
      filters.softScoreId = softScoreId;
    }
    if (round) {
      filters.round = round;
    }
    let apiPairings = await handlePagingAll("/pairings", filters);

    let apiPairingDict = {};
    if (apiPairings?.length) {
      for (let pairing of apiPairings) {
        if (!apiPairingDict[pairing.softScoreId]) {
          apiPairingDict[pairing.softScoreId] = [];
        }
        apiPairingDict[pairing.softScoreId].push(pairing);
      }
    }

    setSoftScorePairings(apiPairingDict);
    return apiPairingDict;
  }
  async function fetchPlayers() {
    let apiPlayers = [];
    if (
      event?.softScores?.filter((el) => {
        if (softScoreId && el.id !== softScoreId) {
          return false;
        }
        return (
          el.listType === "Checked In Players" || el.listType === "All Players"
        );
      })?.length > 0
    ) {
      apiPlayers = await handlePagingAll("/players", {
        eventId: event.id,
        "expand[]": ["army", "subFaction", "character", "team", "user"]
      });
      apiPlayers = apiPlayers.filter((el) => el.id !== player?.id);
      apiPlayers.sort((a, b) => {
        var nameA = userDisplayName(
          a?.firstName || a?.user?.firstName,
          a?.lastName || a?.user?.lastName
        ).toLowerCase();
        var nameB = userDisplayName(
          b?.firstName || b?.user?.firstName,
          b?.lastName || b?.user?.lastName
        ).toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });
    }
    setPlayers(apiPlayers);
    return apiPlayers;
  }
  async function fetchOpponents() {
    let apiOpponents = [];
    if (
      event?.softScores?.filter((el) => {
        if (softScoreId && el.id !== softScoreId) {
          return false;
        }
        return el.listType === "Opponents" || el.roundByRound;
      })?.length > 0
    ) {
      let filters = {
        playerId: player?.id,
        pairingType: "Pairing",
        "expand[]": ["player1", "player2"]
      };
      if (round) {
        filters.round = round;
      }
      var apiPairings = parentParing
        ? [parentParing]
        : await handlePagingAll("/pairings", filters);

      for (let apiPairing of apiPairings) {
        if (apiPairing.player1?.id && apiPairing.player2?.id) {
          apiOpponents.push({
            ...(apiPairing.player1?.id === player?.id
              ? apiPairing.player2
              : apiPairing.player1),
            pairingId: apiPairing.id,
            // TODO: handle team and seated pairings
            round: apiPairing.round,
            table: apiPairing.table
          });
        } else if (softScorePairing?.byePlayer) {
          apiOpponents.push({
            ...(apiPairing.player1?.id
              ? apiPairing.player1
              : apiPairing.player2),
            pairingId: apiPairing.id,
            // TODO: handle team and seated pairings
            round: apiPairing.round,
            table: apiPairing.table
          });
        } else {
          apiOpponents.push({
            pairingId: apiPairing.id,
            // TODO: handle team and seated pairings
            round: apiPairing.round,
            table: apiPairing.table,
            byeRound: true
          });
        }
      }

      apiOpponents.sort((a, b) => {
        var nameA = userDisplayName(
          a?.firstName || a?.user?.firstName,
          a?.lastName || a?.user?.lastName
        ).toLowerCase();
        var nameB = userDisplayName(
          b?.firstName || b?.user?.firstName,
          b?.lastName || b?.user?.lastName
        ).toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });
    }
    setOpponents(apiOpponents);
    return apiOpponents;
  }
  async function parallel() {
    const taskFetchSoftScorePairings = fetchSoftScorePairings();
    const taskFetchPlayers = fetchPlayers();
    const taskFetchOpponents = fetchOpponents();
    return {
      softScorePairings: await taskFetchSoftScorePairings,
      players: await taskFetchPlayers,
      opponents: await taskFetchOpponents
    };
  }

  useEffect(() => {
    const check = async () => {
      try {
        await parallel();
      } catch (err) {
        postNotification("error", err);
      }
      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [event, player]);

  const sumRows = (rows) => {
    let total = 0;
    for (let row of rows) {
      total += row.value;
    }
    return total;
  };

  return (
    <>
      {!loading &&
        event.softScores?.length > 0 &&
        event.softScores
          .filter(
            (el) => el.playerScored && (!softScoreId || el.id === softScoreId)
          )
          .map((softScore) => {
            return (
              <Container key={softScore.id} sx={{ pt: 2, pb: 2 }} maxWidth="lg">
                <Grid
                  container
                  sx={{
                    gap: 2,
                    p: 2,
                    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1,
                    backgroundColor: "background.secondary"
                  }}
                >
                  {!softScoreId && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h4" component="h2">
                          {softScore.name}
                        </Typography>
                      </Grid>

                      {softScore.description && (
                        <Grid item xs={12}>
                          <MarkdownViewer source={softScore.description} />
                        </Grid>
                      )}
                    </>
                  )}

                  {softScore.oncePerEvent &&
                    softScore.scoreOptions?.length > 0 && (
                      <Grid item xs={12}>
                        <Formik
                          initialValues={{
                            pairingType: "SoftScorePairing",
                            eventId: event.id,
                            softScoreId: softScore.id,
                            playerId: player.id,
                            isDone: true,
                            scores: softScore.scoreOptions.map((el, index) => {
                              return {
                                playerId:
                                  softScorePairings?.[softScore.id]?.[0]
                                    ?.scores?.[index]?.playerId || null, // grab from save softScorePairing
                                value: el.value,
                                label: `${
                                  softScorePairings?.[softScore.id]?.[0]
                                    ?.scores?.[index]?.player?.firstName || ""
                                } ${
                                  softScorePairings?.[softScore.id]?.[0]
                                    ?.scores?.[index]?.player?.lastName || ""
                                }`.trim()
                              };
                            })
                          }}
                          enableReinitialize
                          onSubmit={async (values) => {
                            try {
                              setProcessing(true);

                              let sanitizedValues = { ...values };
                              let errors = [];
                              let playerIds = {};

                              for (
                                let i = 0;
                                i < sanitizedValues.scores?.length;
                                i++
                              ) {
                                let error = undefined;
                                if (!sanitizedValues.scores[i]?.playerId) {
                                  error = `Missing Player ${i + 1}`;
                                } else if (
                                  playerIds[sanitizedValues.scores[i]?.playerId]
                                ) {
                                  error = `Duplicate Player ${i + 1}`;
                                } else {
                                  playerIds[
                                    sanitizedValues.scores[i]?.playerId
                                  ] = true;
                                }
                                if (error) {
                                  postNotification("error", error);
                                  errors.push(error);
                                }
                              }
                              if (errors?.length > 0) {
                                setProcessing(false);
                                return;
                              }

                              var apiPairing = await postData("/pairings", {
                                body: sanitizedValues
                              });

                              setSoftScorePairings({
                                ...softScorePairings,
                                [softScore.id]: [apiPairing]
                              });

                              postNotification(
                                "success",
                                `${softScore.name || "Soft Score"} Submitted`
                              );

                              if (setRefreshPlayers) {
                                setRefreshPlayers(true);
                              }
                            } catch (err) {
                              postNotification("error", err);
                            }
                            setProcessing(false);
                          }}
                        >
                          {({
                            errors,
                            handleSubmit,
                            touched,
                            values,
                            setFieldValue
                          }) => (
                            <form onSubmit={handleSubmit}>
                              <FieldArray
                                name="scores"
                                render={() => (
                                  <Grid container spacing={2}>
                                    {softScore.scoreOptions.map(
                                      (scoreOption, index) => {
                                        let optionsArray = (
                                          softScore.listType ===
                                          "Checked In Players"
                                            ? players.filter(
                                                (el) => el.checkedIn
                                              )
                                            : softScore.listType === "Opponents"
                                              ? opponents
                                              : players
                                        )
                                          .filter((el) => !el?.byeRound)
                                          .map((el) => {
                                            return {
                                              playerId: el.id,
                                              value: scoreOption?.value,
                                              label: userDisplayName(
                                                el.firstName ||
                                                  el.user?.firstName,
                                                el.lastName || el.user?.lastName
                                              )
                                            };
                                          });
                                        return (
                                          <Grid
                                            key={`${softScore.id}-${index}`}
                                            item
                                            xs={12}
                                          >
                                            <Autocomplete
                                              id={`scores[${index}]`}
                                              name={`scores[${index}]`}
                                              value={values.scores?.[index]}
                                              disablePortal
                                              autoHighlight
                                              options={optionsArray}
                                              getOptionLabel={(option) =>
                                                option.label
                                              }
                                              onChange={(e, newValue) => {
                                                setFieldValue(
                                                  `scores[${index}]`,
                                                  newValue
                                                );
                                              }}
                                              disabled={
                                                !organize &&
                                                (event?.ended ||
                                                  !softScore?.active)
                                              }
                                              renderInput={(params) => (
                                                <TextField
                                                  {...params}
                                                  label={`Player ${index + 1}`}
                                                  inputProps={{
                                                    ...params.inputProps,
                                                    autoComplete: "new-password"
                                                  }}
                                                  disabled={
                                                    !organize &&
                                                    (event?.ended ||
                                                      !softScore?.active)
                                                  }
                                                />
                                              )}
                                            />
                                          </Grid>
                                        );
                                      }
                                    )}

                                    {Boolean(
                                      touched.scores && errors.scores
                                    ) && (
                                      <Grid item xs={12}>
                                        <FormHelperText error>
                                          {errors.scores}
                                        </FormHelperText>
                                      </Grid>
                                    )}

                                    <Grid item xs={12} textAlign="center">
                                      {!(
                                        !organize &&
                                        (event?.ended || !softScore?.active)
                                      ) ? (
                                        <Button
                                          color="success"
                                          fullWidth
                                          variant="contained"
                                          type="submit"
                                        >
                                          Submit
                                        </Button>
                                      ) : (
                                        <Typography variant="caption">
                                          This soft score is currently not set
                                          to Active.
                                        </Typography>
                                      )}
                                    </Grid>
                                  </Grid>
                                )}
                              />
                            </form>
                          )}
                        </Formik>
                      </Grid>
                    )}

                  {softScore.roundByRound &&
                    !softScore?.roundByRoundOptions?.questions?.length &&
                    opponents?.length > 0 &&
                    opponents
                      .sort((a, b) => a.round - b.round)
                      .filter((el) => {
                        return !(activeRound && el.round !== activeRound);
                      })
                      .map((opponent) => {
                        let sScorePairing = softScorePairings?.[
                          softScore.id
                        ]?.filter(
                          (el) => el.parentPairingId === opponent.pairingId
                        )?.[0];

                        if (opponent.byeRound) {
                          return (
                            <Grid
                              key={`${softScore.id}.${opponent.round}`}
                              item
                              xs={12}
                              sx={{
                                pb: 1,
                                mb: 2,
                                borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                              }}
                              textAlign="center"
                            >
                              <Typography variant="caption">
                                This player had no opponent in round{" "}
                                {opponent.round}
                              </Typography>
                            </Grid>
                          );
                        }
                        return (
                          <Grid
                            key={`${softScore.id}.${opponent.round}`}
                            item
                            xs={12}
                            sx={{
                              pb: 1,
                              mb: 2,
                              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                            }}
                          >
                            <Formik
                              initialValues={{
                                pairingType: "SoftScorePairing",
                                parentPairingId: opponent.pairingId, // TODO: implement team and seated pairings
                                round: opponent.round,
                                eventId: event.id,
                                softScoreId: softScore.id,
                                playerId: player?.id,
                                isDone: sScorePairing?.isDone || false,
                                value:
                                  sScorePairing?.scores?.[0]?.value !==
                                  undefined
                                    ? sScorePairing?.scores?.[0]?.value
                                    : softScore?.roundByRoundOptions
                                        ?.defaultValue || 0
                              }}
                              enableReinitialize
                              validationSchema={Yup.object().shape({
                                value: Yup.number()
                                  .required("Required")
                                  .max(softScore?.roundByRoundOptions?.maxValue)
                                  .min(softScore?.roundByRoundOptions?.minValue)
                              })}
                              onSubmit={async (values) => {
                                try {
                                  setProcessing(true);

                                  let sanitizedValues = {
                                    ...values,
                                    isDone: true,
                                    scores: [
                                      {
                                        playerId: opponent.id,
                                        value: values.value
                                      }
                                    ]
                                  };

                                  if (softScorePairing?.byePlayer) {
                                    sanitizedValues.byePlayer = true;
                                  }

                                  delete sanitizedValues.value;

                                  var apiPairing = await postData("/pairings", {
                                    body: sanitizedValues
                                  });

                                  let newSoftScorePairings =
                                    softScorePairings[softScore.id] || [];
                                  let foundExisting = false;
                                  for (
                                    let i = 0;
                                    i < newSoftScorePairings.length;
                                    i++
                                  ) {
                                    if (
                                      newSoftScorePairings[i].id ===
                                      apiPairing.id
                                    ) {
                                      newSoftScorePairings[i] = apiPairing;
                                      foundExisting = true;
                                      break;
                                    }
                                  }

                                  if (!foundExisting) {
                                    newSoftScorePairings.push(apiPairing);
                                  }
                                  setSoftScorePairings({
                                    ...softScorePairings,
                                    [softScore.id]: newSoftScorePairings
                                  });

                                  postNotification(
                                    "success",
                                    `${
                                      softScore.name || "Soft Score"
                                    } Submitted`
                                  );

                                  if (setRefreshPlayers) {
                                    setRefreshPlayers(true);
                                  }
                                  if (postPairingSubmit) {
                                    postPairingSubmit(apiPairing);
                                  }
                                } catch (err) {
                                  console.log(err.stack);
                                  postNotification("error", err);
                                }
                                setProcessing(false);
                              }}
                            >
                              {({
                                errors,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                isSubmitting,
                                touched,
                                values,
                                setFieldValue,
                                setFieldTouched
                              }) => (
                                <form onSubmit={handleSubmit}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <Typography variant="h4">
                                        Round {opponent.round}:{" "}
                                        {userDisplayName(
                                          opponent.firstName,
                                          opponent.lastName
                                        )}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                      <TextField
                                        name="value"
                                        label="Score"
                                        value={values.value}
                                        variant="outlined"
                                        fullWidth
                                        required
                                        type="number"
                                        onBlur={handleChange}
                                        onChange={handleChange}
                                        error={Boolean(
                                          touched.value && errors.value
                                        )}
                                        helperText={
                                          touched.value && errors.value
                                        }
                                      />
                                    </Grid>

                                    <Grid item xs={12} textAlign="center">
                                      {!(
                                        !organize &&
                                        (event?.ended ||
                                          event.currentRound !== opponent.round)
                                      ) ? (
                                        <Button
                                          color="success"
                                          fullWidth
                                          variant={
                                            values.isDone
                                              ? "outlined"
                                              : "contained"
                                          }
                                          type="submit"
                                        >
                                          {values.isDone ? "Update" : "Submit"}
                                        </Button>
                                      ) : (
                                        <Typography variant="caption">
                                          This soft score can only be scored
                                          during an active round
                                        </Typography>
                                      )}
                                    </Grid>
                                  </Grid>
                                </form>
                              )}
                            </Formik>
                          </Grid>
                        );
                      })}

                  {softScore.roundByRound &&
                    softScore?.roundByRoundOptions?.questions?.length > 0 &&
                    opponents?.length > 0 &&
                    opponents
                      .sort((a, b) => a.round - b.round)
                      .filter((el) => {
                        return !(activeRound && el.round !== activeRound);
                      })
                      .map((opponent) => {
                        let sScorePairing = softScorePairings?.[
                          softScore.id
                        ]?.filter(
                          (el) => el.parentPairingId === opponent.pairingId
                        )?.[0];

                        if (opponent.byeRound) {
                          return (
                            <Grid
                              key={`${softScore.id}.${opponent.round}`}
                              item
                              xs={12}
                              sx={{
                                pb: 1,
                                mb: 2,
                                borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                              }}
                              textAlign="center"
                            >
                              <Typography variant="caption">
                                This player had no opponent in round{" "}
                                {opponent.round}
                              </Typography>
                            </Grid>
                          );
                        }

                        let metaData = [];

                        for (
                          let i = 0;
                          i < softScore.roundByRoundOptions.questions.length;
                          i++
                        ) {
                          metaData[i] = {
                            label:
                              softScore.roundByRoundOptions.questions[i].label,
                            value:
                              sScorePairing?.metaData?.[i]?.value !== undefined
                                ? sScorePairing?.metaData?.[i]?.value
                                : softScore.roundByRoundOptions.questions[i]
                                    .value
                          };
                        }

                        return (
                          <Grid
                            key={`${softScore.id}.${opponent.round}`}
                            item
                            xs={12}
                            sx={{
                              pb: 1,
                              mb: 2,
                              borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
                            }}
                          >
                            <Formik
                              initialValues={{
                                pairingType: "SoftScorePairing",
                                parentPairingId: opponent.pairingId, // TODO: implement team and seated pairings
                                round: opponent.round,
                                eventId: event.id,
                                softScoreId: softScore.id,
                                playerId: player.id,
                                isDone: sScorePairing?.isDone || false,
                                value:
                                  sScorePairing?.scores?.[0]?.value !==
                                  undefined
                                    ? sScorePairing?.scores?.[0]?.value
                                    : 0,
                                metaData: metaData
                              }}
                              enableReinitialize
                              onSubmit={async (values) => {
                                try {
                                  setProcessing(true);

                                  let value = sumRows(values.metaData);

                                  let sanitizedValues = {
                                    ...values,
                                    isDone: true,
                                    scores: [
                                      {
                                        playerId: opponent.id,
                                        value: value
                                      }
                                    ]
                                  };

                                  if (softScorePairing?.byePlayer) {
                                    sanitizedValues.byePlayer = true;
                                  }

                                  delete sanitizedValues.value;

                                  var apiPairing = await postData("/pairings", {
                                    body: sanitizedValues
                                  });

                                  let newSoftScorePairings =
                                    softScorePairings[softScore.id] || [];
                                  let foundExisting = false;
                                  for (
                                    let i = 0;
                                    i < newSoftScorePairings.length;
                                    i++
                                  ) {
                                    if (
                                      newSoftScorePairings[i].id ===
                                      apiPairing.id
                                    ) {
                                      newSoftScorePairings[i] = apiPairing;
                                      foundExisting = true;
                                      break;
                                    }
                                  }

                                  if (!foundExisting) {
                                    newSoftScorePairings.push(apiPairing);
                                  }
                                  setSoftScorePairings({
                                    ...softScorePairings,
                                    [softScore.id]: newSoftScorePairings
                                  });

                                  postNotification(
                                    "success",
                                    `${
                                      softScore.name || "Soft Score"
                                    } Submitted`
                                  );

                                  if (setRefreshPlayers) {
                                    setRefreshPlayers(true);
                                  }
                                  if (postPairingSubmit) {
                                    postPairingSubmit(apiPairing);
                                  }
                                } catch (err) {
                                  console.log(err.stack);
                                  postNotification("error", err);
                                }
                                setProcessing(false);
                              }}
                            >
                              {({
                                errors,
                                handleSubmit,
                                touched,
                                values,
                                setFieldValue
                              }) => (
                                <form onSubmit={handleSubmit}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <Typography variant="h4">
                                        Round {opponent.round}:{" "}
                                        {userDisplayName(
                                          opponent.firstName,
                                          opponent.lastName
                                        )}
                                      </Typography>
                                    </Grid>

                                    {(!sScorePairing?.forceDefaults ||
                                      organize ||
                                      !(
                                        !organize &&
                                        (event?.ended ||
                                          event.currentRound !== opponent.round)
                                      )) && (
                                      <>
                                        <FieldArray
                                          name="metaData"
                                          render={(arrayHelpers) => (
                                            <>
                                              {values.metaData.map(
                                                (metaDataRow, index) => {
                                                  return (
                                                    <Grid
                                                      key={`${softScore.id}-metaDataRow-${index}`}
                                                      item
                                                      xs={12}
                                                    >
                                                      <FormControlLabel
                                                        label={
                                                          softScore
                                                            .roundByRoundOptions
                                                            .questions[index]
                                                            ?.label
                                                        }
                                                        sx={{
                                                          ml: 0,
                                                          width: "100%",
                                                          "& .MuiFormControlLabel-label":
                                                            {
                                                              width:
                                                                "calc(100% - 58px)"
                                                            }
                                                        }}
                                                        control={
                                                          <Switch
                                                            checked={
                                                              values.metaData[
                                                                index
                                                              ]?.value !== 0
                                                            }
                                                            onChange={(e) => {
                                                              setFieldValue(
                                                                `metaData[${index}].value`,
                                                                values.metaData[
                                                                  index
                                                                ]?.value !== 0
                                                                  ? 0
                                                                  : softScore
                                                                      .roundByRoundOptions
                                                                      .questions[
                                                                      index
                                                                    ]?.value
                                                              );
                                                            }}
                                                          />
                                                        }
                                                        labelPlacement="start"
                                                      />
                                                    </Grid>
                                                  );
                                                }
                                              )}

                                              {Boolean(
                                                touched.scores && errors.scores
                                              ) && (
                                                <Grid item xs={12}>
                                                  <FormHelperText error>
                                                    {errors.scores}
                                                  </FormHelperText>
                                                </Grid>
                                              )}
                                            </>
                                          )}
                                        />

                                        <Grid item xs={12} textAlign="right">
                                          <Typography>
                                            Total: {sumRows(values.metaData)}
                                          </Typography>
                                        </Grid>
                                      </>
                                    )}

                                    {sScorePairing?.forceDefaults && (
                                      <Grid item xs={12} textAlign="center">
                                        <Typography color="warning.main">
                                          This soft score currently has a
                                          default override of{" "}
                                          {sScorePairing?.scores?.[0]?.value}.
                                          {organize &&
                                            " Please submit a score."}
                                        </Typography>
                                      </Grid>
                                    )}

                                    <Grid item xs={12} textAlign="center">
                                      {!(
                                        !organize &&
                                        (event?.ended ||
                                          event.currentRound !== opponent.round)
                                      ) ? (
                                        <Button
                                          color="success"
                                          fullWidth
                                          variant={
                                            values.isDone
                                              ? "outlined"
                                              : "contained"
                                          }
                                          type="submit"
                                        >
                                          {values.isDone ? "Update" : "Submit"}
                                        </Button>
                                      ) : (
                                        <Typography variant="caption">
                                          This soft score can only be scored
                                          during an active round
                                        </Typography>
                                      )}
                                    </Grid>
                                  </Grid>
                                </form>
                              )}
                            </Formik>
                          </Grid>
                        );
                      })}
                </Grid>
              </Container>
            );
          })}

      {(loading || processing) && <ModalLoadingFull />}
    </>
  );
}
